import { BatteryABS } from "./battery/BatteryABS";
import { LocationData } from "./LocationData";
import { ILocation, Location } from "./Location";
import { LocationDto } from "./LocationDTO";

/**
 * Location type representing a standalone battery installation with no
 * associated Skid or EMS portal device — the "EXC 241" fleet.
 *
 * The location owns exactly one battery row. Each poll of
 * `FreestandingBatteryController::loadBatteries()` writes the same reading
 * twice: to `battery_data` (per battery) and to `location_data` (aggregated).
 * The getters below read whichever of the two is authoritative and adapt this
 * type to the `FleetDevice` contract `<app-fleet-overview>` renders.
 */
export class FreestandingBattery extends Location {
  /** The single battery behind this location. */
  get battery(): BatteryABS | undefined {
    return this.batteries?.[0];
  }

  /**
   * FleetDevice contract: the aggregated snapshot, which carries the charge
   * state and the timestamp the shell uses for age and staleness.
   */
  get lastData(): LocationData | undefined {
    return this.location_data_latest;
  }

  /** State of charge (%); null when the battery has never reported. */
  get soc(): number | null {
    return this.battery?.battery_data_latest?.soc ?? null;
  }

  /** Signed power in kW, `+` charging / `−` discharging as reported. */
  get powerKw(): number | null {
    const watts = this.battery?.battery_data_latest?.power_w;
    return watts == null ? null : watts / 1000;
  }

  /** Cell temperature in °C; null when the battery has never reported. */
  get temperature(): number | null {
    return this.battery?.battery_data_latest?.temperature ?? null;
  }

  /**
   * Installed capacity in kWh. Stored as 0 when left unconfigured, which is not
   * a meaningful capacity — normalise it to null so callers can just falsy-check.
   */
  get capacityKwh(): number | null {
    return this.battery?.maximum_capacity || null;
  }

  /**
   * Energy currently stored (kWh), from SOC against the configured capacity.
   * Null unless both are known, so the ring caption can fall back to the plain
   * nameplate capacity rather than showing a made-up number.
   */
  get storedKwh(): number | null {
    const soc = this.soc;
    const capacity = this.capacityKwh;
    return soc == null || capacity == null ? null : (capacity * soc) / 100;
  }

  public static FromDto(dto: LocationDto): FreestandingBattery {
    return Location.fromDto(new FreestandingBattery(), dto);
  }

  public FromDto(dto: LocationDto): ILocation {
    return FreestandingBattery.FromDto(dto);
  }
}
